import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Input } from '@thd-olt-component-react/input';
import { Loader } from '@thd-olt-component-react/core-ui';
import Field from '../Field/Field';
import { generateImageSource } from '../../../utils/generateImageSource';
import { useStore } from '../../../store';
import { SuggestedResultsDropDown } from './SuggestedResultsDropDown';
import { LOCATION_SERVICE_FAILED_LOCAL_TEXT } from '../../../utils/constants';

const _SuggestedLocationInput = ({
  locationText,
  setLocationText,
  // locationTextError is a boolean flag, not a string with error text
  locationTextError,
  validation,
  setLocationTextError,
  setLocationObject,
  label,
  onSelectCallback,
  fetchingInitialLocation,
  InFilter
}) => {
  // Mobx
  const { locationServiceStore: {
    getLocations,
    locations,
    clearLocations,
    isFetching,
    updateIsFetching
  },
  filterStore: {
    removeFilterParam
  } } = useStore();

  // Local state
  const [isEditing, updateIsEditing] = useState(false);
  const [searchQuery, updateSearchQuery] = useState(locationText);
  const [moreResults, updateMoreResults] = useState(null);
  const [isHovering, updateIsHovering] = useState(false);

  // location comes in empty which is what it gets initialized to
  // this ensures that it gets reset
  useEffect(() => {
    updateSearchQuery(locationText);
  }, [locationText]);

  function placeholderText() {
    if (fetchingInitialLocation) {
      return 'Checking current location...';
    }
    if (InFilter) {
      return 'Type city or zip code';
    }
    return 'Type in a zip code';
  }

  return (
    <Field
      {... label && { label }}
      required
      errorText={locationTextError ? LOCATION_SERVICE_FAILED_LOCAL_TEXT : ''}
      showError={locationTextError && !isEditing}
    >
      <div id="locationServiceWrapper" className="suggested-location-input__location-service-wrapper">
        <div id="location-service" className="suggested-location-input__icon-wrapper" data-testid="suggested-location-input">
          <Input
            placeholder={placeholderText()}
            required
            validateOnBlur
            onFocus={initEdit}
            onBlur={endEdit}
            value={isEditing ? searchQuery : locationText}
            onChange={updateSearchQueryFromInput}
            disabled={fetchingInitialLocation}
          />
          <div className="suggested-location-input__icon-holder">
            <EndIcons isFetching={isFetching} />
          </div>
        </div>
        <SuggestedResultsDropDown
          setLocationText={updateLocation}
          setLocationObject={setLocationObject}
          validation={validation}
          moreResults={moreResults}
          results={locations}
          isEditing={isEditing}
          updateIsHovering={updateIsHovering}
          searchQuery={searchQuery}
        />
      </div>
    </Field>
  );

  function updateLocation(newLocation) {
    setLocationText(newLocation);
    clearLocations();
    updateIsEditing(false);
    if (onSelectCallback) {
      onSelectCallback();
    }
  }

  function initEdit(e) {
    if (locationTextError) {
      setLocationTextError(false);
    } else {
      updateSearchQueryFromInput(e.value);
    }
    updateIsEditing(true);
  }

  async function updateSearchQueryFromInput(value) {
    updateSearchQuery(value);

    if (value.length >= 3) {
      updateIsFetching(true);
      if (InFilter) {
        await getLocations(value, false);
      } else {
        await getLocations(value, true);
      }
      updateIsFetching(false);
    } else {
      setLocationText('');
      updateMoreResults(null);
      removeFilterParam('location', value);
    }
  }

  function endEdit() {
    if (isHovering) {
      return false;
    }
    updateIsEditing(false);
    clearLocations();
  }
};

function EndIcons({ isFetching }) {

  return (
    <>
      {isFetching
        ? (
          <span data-testid="suggested-location-loader">
            <Loader active className="suggested-location-input__loader-icon" />
          </span>
        )
        : (
          <img
            className="suggested-location-input__location-icon"
            src={generateImageSource('/images/v1/pin-orange.svg')}
          />
        )}
    </>
  );
}

export const SuggestedLocationInput = observer(_SuggestedLocationInput);
